import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setPlayerStats } from '../../../store/playerSlice';
import BackButton from '../../Shared/BackButton';
import StatRow from '../../Shared/StatRow';
import CupBoard from '../../Shared/CupBoard/CupBoard';
import styles from './PlayerStats.module.css';

const PlayerStats = () => {
    const player = useSelector((state) => state.player);
    const username = useSelector((state) => state.auth.username || state.user.username);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (player.cups.length >= 10 || !username) return;
        axios.get(`${process.env.REACT_APP_API_URL}/api/c/getstats/`, { params: { username } })
            .then(response => {
                const p = response.data.player_stats || {};
                dispatch(setPlayerStats({
                    totalGamesPlayed: p.total_games_played,
                    totalGamesCompleted: p.total_games_completed,
                    wins: p.wins,
                    losses: p.losses,
                    winningPercentage: p.winning_percentage,
                    saves: p.saves,
                    savesPerGame: p.saves_per_game,
                    halfcups: p.half_cups,
                    halfcupsPerGame: p.half_cups_per_game,
                    fullCups: p.full_cups,
                    fullCupsPerGame: p.full_cups_per_game,
                    ownCups: p.own_cups,
                    ownCupsPerGame: p.own_cups_per_game,
                    totalCups: p.total_cups,
                    totalCupsPerGame: p.total_cups_per_game,
                    lapsGiven: p.laps_given,
                    lapsReceived: p.laps_received,
                    totalCupsFor: p.total_cups_for,
                    totalCupsAgainst: p.total_cups_against,
                    cups: p.cups,
                }));
            })
            .catch(() => {});
    }, [username, dispatch]);  // eslint-disable-line react-hooks/exhaustive-deps

    if (!player || player.cups.length < 10) return <p className={styles.loadingText}>Loading player statistics...</p>;

    const avgFor = isNaN(player.totalCupsFor / player.totalGamesCompleted)
        ? '0.00'
        : (player.totalCupsFor / player.totalGamesCompleted).toFixed(2);

    const avgAgainst = isNaN(player.totalCupsAgainst / player.totalGamesCompleted)
        ? '0.00'
        : (player.totalCupsAgainst / player.totalGamesCompleted).toFixed(2);

    const netCupDiff = isNaN(player.totalCupsFor / player.totalGamesCompleted)
        ? '0.00'
        : ((player.totalCupsFor / player.totalGamesCompleted) - (player.totalCupsAgainst / player.totalGamesCompleted)).toFixed(2);

    const cupsAndGames = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/c/getteamstats/`, {
                params: { username: username },
            });

            const cups = {
                off_fr: response.data.stats.cups.off_fr?.value ?? 0,
                off_lf: response.data.stats.cups.off_lf?.value ?? 0,
                off_rt: response.data.stats.cups.off_rt?.value ?? 0,
                off_ct: response.data.stats.cups.off_ct?.value ?? 0,
                off_bk: response.data.stats.cups.off_bk?.value ?? 0,
                def_fr: response.data.stats.cups.def_fr?.value ?? 0,
                def_lf: response.data.stats.cups.def_lf?.value ?? 0,
                def_rt: response.data.stats.cups.def_rt?.value ?? 0,
                def_ct: response.data.stats.cups.def_ct?.value ?? 0,
                def_bk: response.data.stats.cups.def_bk?.value ?? 0,
            };

            const games = response.data.stats.recentGames ?? [];
            return { cups, games };
        } catch (error) {
            console.error("Error fetching cups and games:", error);
            return {
                cups: {
                    off_fr: 0, off_lf: 0, off_rt: 0, off_ct: 0, off_bk: 0,
                    def_fr: 0, def_lf: 0, def_rt: 0, def_ct: 0, def_bk: 0,
                },
                games: [],
            };
        }
    };

    const handleSeePlayerScoreBoard = async () => {
        try {
            const data = await cupsAndGames();
            navigate('playerscoreboard', { state: { cups: data.cups, games: data.games } });
        } catch (error) {
            console.error("Error fetching data for scoreboard:", error);
        }
    };

    return (
        <div className={styles.container}>
            <BackButton />

            <h1 className={styles.title}>{player.username} Stats</h1>
            <p className={styles.averageScore}>Avg Game: {avgFor} – {avgAgainst}</p>

            <div className={styles.statsBoard}>

                <div className={styles.statsSection}>
                    <p className={styles.sectionTitle}>General</p>
                    <StatRow label="Total Games" value={player.totalGamesCompleted} />
                    <StatRow label="Win %" value={`${(player.winningPercentage * 100).toFixed(2)}%`} />
                    <StatRow label="Net Individual Cups Hit / Game" value={(player.totalCupsPerGame - player.ownCupsPerGame).toFixed(2)} />
                    <StatRow label="Net Team Cup +/-" value={netCupDiff} />
                    <StatRow label="Saves / Game" value={isNaN(player.savesPerGame) ? '0.00' : player.savesPerGame.toFixed(2)} />
                </div>

                {/* Shot Chart */}
                <div className={styles.cupBoardWrapper}>
                    <p className={styles.cupBoardLabel}>Shot Chart — heat map, hover for avg</p>
                    <p className={styles.cupBoardSideLabel}>Opponents</p>
                    <CupBoard
                        variant="full"
                        showHeat
                        attackData={{
                            fr: player.cups[0].value,
                            lf: player.cups[2].value,
                            rt: player.cups[4].value,
                            ct: player.cups[6].value,
                            bk: player.cups[8].value,
                        }}
                        defenseData={{
                            fr: player.cups[1].value,
                            lf: player.cups[3].value,
                            rt: player.cups[5].value,
                            ct: player.cups[7].value,
                            bk: player.cups[9].value,
                        }}
                    />
                    <p className={styles.cupBoardSideLabel}>{username}</p>
                    <div className={styles.legend}>
                        <div className={styles.legendItem}>
                            <div className={`${styles.legendDot} ${styles.legendDotRed}`}></div>
                            <span>Attacking</span>
                        </div>
                        <div className={styles.legendItem}>
                            <div className={`${styles.legendDot} ${styles.legendDotBlue}`}></div>
                            <span>Own cups</span>
                        </div>
                    </div>
                </div>

                <div className={styles.statsSection}>
                    <p className={styles.sectionTitle}>Per Game</p>
                    <StatRow label="Team Cups For" value={avgFor} />
                    <StatRow label="Team Cups Against" value={avgAgainst} />
                    <StatRow label="Half Cups Hit" value={player.halfcupsPerGame.toFixed(2)} />
                    <StatRow label="Full Cups Hit" value={player.fullCupsPerGame.toFixed(2)} />
                    <StatRow label="Total Cups Hit" value={player.totalCupsPerGame.toFixed(2)} />
                    <StatRow label="Own Cups" value={player.ownCupsPerGame.toFixed(2)} />
                </div>

                <div className={styles.statsSection}>
                    <p className={styles.sectionTitle}>Totals</p>
                    <StatRow label="Games Played" value={player.totalGamesCompleted} />
                    <StatRow label="Wins" value={player.wins} />
                    <StatRow label="Losses" value={player.losses} />
                    <StatRow label="Half Cups Hit" value={player.halfcups} />
                    <StatRow label="Full Cups Hit" value={player.fullCups} />
                    <StatRow label="Total Cups Hit" value={player.totalCups} />
                    <StatRow label="Own Cups" value={player.ownCups} />
                    <StatRow label="Total Saves" value={player.saves} />
                    <StatRow label="Laps Given" value={player.lapsGiven} />
                    <StatRow label="Laps Received" value={player.lapsReceived} />
                    <StatRow label="Team Cups For" value={player.totalCupsFor} />
                    <StatRow label="Team Cups Against" value={player.totalCupsAgainst} />
                </div>

                <button className={styles.advancedButton} onClick={handleSeePlayerScoreBoard}>
                    Advanced Stats
                </button>

            </div>
        </div>
    );
};

export default PlayerStats;
